import React from 'react';
import { Task } from '../../types/todo';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { scrollToTop } from '../../utils/scroll';
import { MapPin, Calendar, Wallet, Globe2, Heart, X, MessageSquare, ExternalLink, Shield, CheckCircle2, Clock, XCircle, ChevronLeft, ChevronRight, Info, ChevronDown, ArrowLeft, User, Settings, Camera } from 'lucide-react';
import { Link } from 'react-router-dom';
import { translateTravelStyle } from '../../utils/matching';
import { doc, getDoc, updateDoc, arrayRemove, arrayUnion } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import * as amplitude from '@amplitude/analytics-browser';
import { budgetCountries } from '../../data/budget-countries';

interface MatchingCardProps {
  profile: TravelProfile;
  onLike: () => void;
  onPass: () => void;
  onMessage?: () => void;
  similarity?: number;
  isOwnProfile?: boolean;
  currentUserId?: string;
}

export function MatchingCard({ 
  profile, 
  onLike, 
  onPass, 
  onMessage, 
  similarity, 
  isOwnProfile, 
  currentUserId 
}: MatchingCardProps) {
  const [currentPhotoIndex, setCurrentPhotoIndex] = React.useState(0);
  const [touchStart, setTouchStart] = React.useState<number | null>(null);
  const [touchEnd, setTouchEnd] = React.useState<number | null>(null);
  const [localProfile, setLocalProfile] = React.useState(profile);
  const [viewCount, setViewCount] = React.useState(profile.viewCount || 0);
  const [cachedPhoto, setCachedPhoto] = React.useState<string | null>(null);
  const [isLiked, setIsLiked] = React.useState(false);
  const [isLiking, setIsLiking] = React.useState(false);
  const [showBio, setShowBio] = React.useState(false);
  const [showPreferences, setShowPreferences] = React.useState(true);
  const commentCount = profile.comments?.length || 0;
  const likesCount = localProfile.likes?.length || 0;
  
  // Get country flag
  const countryFlag = React.useMemo(() => {
    if (!profile.preferences.destination) return null;
    return budgetCountries.find(c => c.code === profile.preferences.destination)?.flag || '🌍';
  }, [profile.preferences.destination]);

  // Fetch cached photo when profile loads
  React.useEffect(() => {
    const fetchCachedPhoto = async () => {
      if (profile.preferences.city) {
        try {
          const cacheRef = doc(db, 'photo_cache', profile.preferences.city.toLowerCase());
          const cacheDoc = await getDoc(cacheRef);
          
          if (cacheDoc.exists()) {
            const data = cacheDoc.data();
            const photos = data.photos;
            if (photos && photos.length > 0) {
              // Sélectionner une photo aléatoire du cache
              const randomPhoto = photos[Math.floor(Math.random() * photos.length)];
              setCachedPhoto(randomPhoto.url);
            }
          }
        } catch (error) {
          console.error('Error fetching cached photo:', error);
        }
      }
    };

    fetchCachedPhoto();
  }, [profile.preferences.city]);

  // Check if the current user has liked this profile
  React.useEffect(() => {
    if (currentUserId && localProfile.likes) {
      setIsLiked(localProfile.likes.includes(currentUserId));
    }
  }, [currentUserId, localProfile.likes]);

  // Get destination image for background
  const getDestinationImage = (countryCode: string): string => {
    const country = budgetCountries.find(c => c.code === countryCode);
    return country?.image || 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?q=80&w=2670&auto=format&fit=crop';
  };

  // Update local profile when profile changes
  React.useEffect(() => {
    setLocalProfile(profile);
    setViewCount(profile.viewCount || 0);
  }, [profile]);

  // Calculate age from birthDate
  const getAge = (birthDate: any) => {
    if (!birthDate) return null;
    let date;
    try {
      // Try toDate() first for Firestore Timestamps
      date = birthDate.toDate?.() || new Date(birthDate);
    } catch (error) {
      // If toDate() fails, try creating a new date
      date = new Date(birthDate);
    }
    return Math.floor((new Date().getTime() - date.getTime()) / (1000 * 60 * 60 * 24 * 365.25));
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    // Don't prevent default to allow scrolling
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
    // Don't prevent default to allow scrolling
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      // Next photo
      if (localProfile.photos && localProfile.photos.length > 1) {
        setCurrentPhotoIndex((prev) => (prev + 1) % localProfile.photos.length);
      } else {
        onPass();
      }
    } else if (isRightSwipe) {
      // Previous photo
      if (localProfile.photos && localProfile.photos.length > 1) {
        setCurrentPhotoIndex((prev) => (prev - 1 + (localProfile.photos?.length || 1)) % (localProfile.photos?.length || 1));
      } else {
        onLike();
      }
    }
  };

  const handleUsernameClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(`https://pvtistes.net/membre/${localProfile.username}/`, '_blank', 'noopener,noreferrer');
  };

  const handleToggleLike = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!currentUserId || isLiking || isOwnProfile) return;
    
    try {
      setIsLiking(true);
      const profileRef = doc(db, 'travel_profiles', profile.id);
      
      // Optimistically update UI
      setIsLiked(prevLiked => !prevLiked);
      
      // Animate heart
      const heartEl = document.getElementById(`heart-${profile.id}`);
      if (heartEl) {
        heartEl.classList.add('scale-125');
        setTimeout(() => heartEl.classList.remove('scale-125'), 300);
      }
      
      if (isLiked) {
        // Unlike
        await updateDoc(profileRef, {
          likes: arrayRemove(currentUserId)
        });
        
        // Update local state
        setLocalProfile(prev => {
          const newLikes = (prev.likes || []).filter(id => id !== currentUserId);
          return { ...prev, likes: newLikes };
        });
        
        // Analytics
        amplitude.track('PVT Mate Unlike', { profileId: profile.id });
      } else {
        // Like
        await updateDoc(profileRef, {
          likes: arrayUnion(currentUserId)
        });
        
        // Update local state
        setLocalProfile(prev => {
          const newLikes = [...(prev.likes || []), currentUserId];
          return { ...prev, likes: newLikes };
        });
        
        // Analytics
        amplitude.track('PVT Mate Like', { profileId: profile.id });
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      // Revert UI on error
      setIsLiked(prevLiked => !prevLiked);
      
      // Show error toast
      const toast = document.createElement('div');
      toast.className = 'fixed bottom-4 left-1/2 -translate-x-1/2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 px-6 py-3 rounded-xl shadow-lg border border-red-100 dark:border-red-800/30 z-50 flex items-center gap-3 animate-fade-in';
      toast.innerHTML = `
        <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <span>Une erreur est survenue</span>
      `;
      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 3000);
    } finally {
      setIsLiking(false);
    }
  };

  const handleNextPhoto = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (localProfile.photos && localProfile.photos.length > 1) {
      setCurrentPhotoIndex((prev) => (prev + 1) % localProfile.photos.length);
    }
  };

  const handlePrevPhoto = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (localProfile.photos && localProfile.photos.length > 1) {
      setCurrentPhotoIndex((prev) => (prev - 1 + (localProfile.photos?.length || 1)) % (localProfile.photos?.length || 1));
    }
  };

  const toggleBio = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowBio(true);
    setShowPreferences(false);
  };

  const togglePreferences = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowBio(false);
    setShowPreferences(true);
  };

  return (
    <Link
      to={`/matching/${profile.id}`}
      onClick={() => scrollToTop()}
      className={`group relative overflow-hidden w-full transform transition-all duration-300 hover:-translate-y-0.5 rounded-xl ${
        localProfile.isOwnProfile
          ? 'bg-gradient-to-br from-primary-50 to-accent-50 dark:from-primary-900/20 dark:to-accent-900/20 shadow-premium border border-primary-100/50 dark:border-primary-800/30'
          : 'bg-white dark:bg-gray-800 shadow-sm hover:shadow-md border border-gray-200/50 dark:border-gray-700/50'
      }`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
        {/* Decorative gradient orb */}
        {isOwnProfile && (
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-primary-100/20 to-accent-100/20 dark:from-primary-800/10 dark:to-accent-800/10 rounded-full transform translate-x-32 -translate-y-32 blur-3xl pointer-events-none" />
        )}

        {/* Photos Carousel */}
        <div className="relative aspect-[3/2] sm:aspect-[16/9] bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 overflow-hidden">
          {/* Always show destination image as background */}
          {cachedPhoto ? (
            <img
              src={cachedPhoto}
              alt={profile.preferences.city}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <img
              src={getDestinationImage(profile.preferences.destination)}
              alt={profile.preferences.city}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          )}
          
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          
          {/* Stats & Like Button Overlay */}
          <div className="absolute top-3 left-0 right-0 flex items-center justify-between px-3 z-20">
            {/* Stats Container */}
            <div className="flex items-center gap-2">
              {/* Views with eye icon */}
              <div className="flex items-center gap-1.5 px-2 py-1 bg-black/40 backdrop-blur-[2px] rounded-lg border border-white/10 text-center">
                <svg className="w-3 h-3 text-white/70" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                <span className="text-xs text-white/90">{viewCount}</span>
              </div>
              
              {/* Photo count indicator */}
              {profile.photos?.length > 0 && (
                <div className="flex items-center gap-1.5 px-2 py-1 bg-black/40 backdrop-blur-[2px] rounded-lg border border-white/10 text-center ml-1">
                  <Camera className="w-3 h-3 text-white/70" />
                  <span className="text-xs text-white/90">{profile.photos.length}</span>
                </div>
              )}
            </div>
            
            {/* Like Button */}
            {!isOwnProfile && currentUserId && (
              <div className="flex items-center gap-1.5 z-20">
                <button
                  onClick={handleToggleLike}
                  className={`flex items-center gap-1 px-2 py-1.5 rounded-lg backdrop-blur-[2px] border transition-all duration-200 z-20 ${
                    isLiked 
                      ? 'bg-pink-500/40 border-pink-400/30 text-white' 
                      : 'bg-black/40 border-white/10 text-white/90 hover:bg-black/50'
                  }`}
                  aria-label={isLiked ? 'Unlike profile' : 'Like profile'}
                  disabled={isLiking}
                >
                  <Heart 
                    id={`heart-${profile.id}`}
                    className={`w-3.5 h-3.5 transition-transform ${isLiked ? 'fill-white' : ''}`}
                  />
                  {likesCount > 0 && (
                    <span className="text-xs">{likesCount}</span>
                  )}
                </button>
                
                {commentCount > 0 && (
                  <div className="px-2 py-1 bg-black/40 backdrop-blur-[2px] rounded-lg border border-white/10 text-center flex items-center gap-1.5 z-20">
                    <MessageSquare className="w-3.5 h-3.5 text-white/70" />
                    <span className="text-xs text-white/90">{commentCount}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Basic Info Overlay */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-3 sm:p-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <h3 className="text-base sm:text-lg font-semibold text-white line-clamp-2">
                  {localProfile.firstName}, {getAge(localProfile.birthDate)} ans
                </h3>
                {localProfile.isOwnProfile && (
                  <span className="text-xs bg-primary-500/20 text-white px-2 py-0.5 rounded-full border border-white/20 backdrop-blur-sm">
                    Mon profil
                  </span>
                )}
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-y-1 sm:gap-x-3">
                <div className="flex items-center gap-1.5 text-white/90">
                  <MapPin className="w-3.5 h-3.5" />
                  <span className="text-xs sm:text-sm">De {localProfile.location}</span>
                </div>
                <div className="flex items-center gap-1.5 text-white/90">
                  <Globe2 className="w-3.5 h-3.5" />
                  <span className="text-xs sm:text-sm flex items-center">
                    Vers {localProfile.preferences.city}, {countryFlag && <span className="ml-1 text-base">{countryFlag}</span>}
                  </span>
                </div>
              </div>
              
              {/* Profile link added here */}
              {localProfile.username && (
                <div className="flex items-center gap-1.5 text-white/80 mt-1">
                  <a 
                    href={`https://pvtistes.net/membre/${localProfile.username}/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleUsernameClick}
                    className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg transition-colors border border-white/10 text-xs"
                  >
                    <User className="w-3 h-3" />
                    <span>Profil pvtistes.net</span>
                    <ExternalLink className="w-2.5 h-2.5" />
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="p-3 sm:p-4 relative">
          {/* Navigation Tabs - MODIFIED: Added icons to make buttons more obvious */}
          <div className="flex items-center justify-between mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2">
            <button
              onClick={togglePreferences}
              className={`flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${
                showPreferences 
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' 
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
              }`}
            >
              <Settings className="w-3.5 h-3.5" />
              <span>Infos</span>
            </button>
            <button
              onClick={toggleBio}
              className={`flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${
                showBio 
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' 
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
              }`}
            >
              <User className="w-3.5 h-3.5" />
              <span>Bio</span>
            </button>
          </div>

          {/* Content Area */}
          <div className="min-h-[120px]">
            {/* Travel Preferences */}
            {showPreferences && (
              <div className="space-y-3 animate-fade-in">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-xs text-gray-600 dark:text-gray-400">Départ prévu</span>
                  </div>
                  <span className="text-xs font-medium text-gray-900 dark:text-white">
                    {format(
                      localProfile.preferences.startDate.toDate?.() || new Date(localProfile.preferences.startDate),
                      'MMMM yyyy',
                      { locale: fr }
                    )}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-xs text-gray-600 dark:text-gray-400">Statut du PVT</span>
                  </div>
                  <div>
                    {localProfile.pvtStatus === 'obtained' ? (
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 rounded-full border border-emerald-100/50 dark:border-emerald-800/30 text-xs font-medium">
                        <CheckCircle2 className="w-3 h-3" />
                        <span>Obtenu</span>
                      </span>
                    ) : localProfile.pvtStatus === 'pending' ? (
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 rounded-full border border-amber-100/50 dark:border-amber-800/30 text-xs font-medium">
                        <Clock className="w-3 h-3" />
                        <span>En cours</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-gray-50 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 rounded-full border border-gray-200/50 dark:border-gray-700/30 text-xs font-medium">
                        <XCircle className="w-3 h-3" />
                        <span>Non demandé</span>
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Globe2 className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-xs text-gray-600 dark:text-gray-400">Style de voyage</span>
                  </div>
                  <span className="text-xs font-medium text-gray-900 dark:text-white">
                    {translateTravelStyle(localProfile.preferences.style)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Wallet className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-xs text-gray-600 dark:text-gray-400">Budget mensuel</span>
                  </div>
                  <span className="text-xs font-medium text-gray-900 dark:text-white">
                    {localProfile.preferences.budget.min.toLocaleString('fr-FR')} - {localProfile.preferences.budget.max.toLocaleString('fr-FR')} €
                  </span>
                </div>
              </div>
            )}
            
            {/* Bio Section */}
            {showBio && (
              <div className="animate-fade-in">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 sm:p-4 border border-gray-200/50 dark:border-gray-700/50">
                  <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed break-words max-h-[150px] overflow-y-auto custom-scrollbar">
                    {localProfile.bio}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
    </Link>
  );
}